import { Box, Collapse, Grid, IconButton, Paper, Typography } from "@material-ui/core";
import { Remove, Add } from "@material-ui/icons";
import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { currencyFormatter, selectCleverlynk } from "../../../../redux/slices/cleverlynkSlice";
import { selectCurrency } from "../../../../redux/slices/templateSlice";

interface IMultipleOptionButton {
  disableAddition: (q: number) => boolean;
  disableSubstraction: boolean;
  name: string;
  value: any;
  modifyRemainingChoices: Function;
  textFontFamily: string;
  outOfStock: (q: number) => boolean;
}

function isNumber(value) {
  return typeof value === "number" && isFinite(value);
}

const MultipleOptionButton = ({
  disableAddition,
  disableSubstraction,
  name,
  value,
  modifyRemainingChoices,
  textFontFamily,
  outOfStock,
}: IMultipleOptionButton) => {
  const [currentValue, setCurrentValue] = useState<number>(0);
  const cleverlynk = useSelector(selectCleverlynk);

  const calculatePriceWithoutDiscount = useCallback(
    value => {
      if (cleverlynk?.discount && value && value > 0) {
        return (100 * value) / (100 - cleverlynk?.discount);
      }
      return 0;
    },
    [cleverlynk]
  );
  const disableAdd = React.useMemo(() => {
    return disableAddition(currentValue);
  }, [currentValue, disableAddition]);

  const memoOutOfStock = React.useMemo(() => {
    return outOfStock(currentValue);
  }, [currentValue, disableAddition]);

  const currency = useSelector(selectCurrency);

  return (
    <Grid
      container
      spacing={2}
      justifyContent="space-between"
      alignItems={"center"}
      style={{ width: "100%", margin: "0px", padding: 5, paddingLeft: 0 }}>
      <Grid item xs={7} style={{ paddingLeft: 0 }}>
        <Box style={{ display: "flex", alignItems: "baseline" }}>
          <Typography
            align="left"
            style={{
              marginRight: 7,
              wordBreak: "break-word",
              textAlign: "left",
              fontFamily: textFontFamily,
              fontSize: "0.9rem",
            }}>
            {name}
          </Typography>
          {cleverlynk?.discount && (
            <Typography
              variant="overline"
              style={{
                fontFamily: textFontFamily,
                paddingRight: 5,
                textAlign: "left",
                opacity: 0.7,
                MozOpacity: 0.7,
                textDecoration: "line-through",
                // paddingRight: 8,
                textDecorationColor: "rgba(250, 0, 0, 1)",
              }}>
              {isNumber(value) && value! > 0
                ? `${currencyFormatter(calculatePriceWithoutDiscount(value!), currency)}`
                : ""}
            </Typography>
          )}
          <Typography variant="overline" align="left" style={{ fontFamily: textFontFamily }}>
            {isNumber(value) && value > 0 ? ` +${currencyFormatter(value, currency)}` : ""}
          </Typography>
        </Box>
      </Grid>
      <Grid
        container
        item
        xs={4}
        spacing={0}
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
        style={{ padding: 0 }}>
        <Paper
          style={{
            backgroundColor: currentValue === 0 ? "#00000000" : "whitesmoke",
            boxShadow: currentValue === 0 ? "none" : "",
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}>
          <Grid item xs={4}>
            {currentValue > 0 && (
              <IconButton
                style={{
                  padding: 2,
                  opacity: disableSubstraction || currentValue < 1 ? "0.1" : 1,
                  backgroundColor: "#00000000",
                  color: disableSubstraction ? "#ccc" : "#444040",
                }}
                disabled={disableSubstraction || currentValue < 1}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (currentValue > 0) {
                    setCurrentValue(p => {
                      modifyRemainingChoices(1, p - 1);
                      return p - 1;
                    });
                  }
                }}>
                <Remove />
              </IconButton>
            )}
          </Grid>
          {currentValue > 0 && (
            <Grid item xs={5} container justifyContent="center" alignItems="center">
              <Typography style={{ fontWeight: "bold", color: "#444040" }}>{currentValue}</Typography>
            </Grid>
          )}
          <Grid item xs={4}>
            <IconButton
              style={{
                padding: 2,
                opacity: disableAdd ? "0.1" : 1,
                backgroundColor: "#00000000",
                color: disableAdd ? "#ccc" : "#444040",
              }}
              color={currentValue === 0 ? "secondary" : "default"}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                setCurrentValue(p => {
                  modifyRemainingChoices(-1, p + 1);
                  return p + 1;
                });
              }}
              disabled={disableAdd}>
              <Add />
            </IconButton>
          </Grid>
          {currentValue === 0 && <Grid item xs={4}></Grid>}
        </Paper>
      </Grid>
      <Collapse in={memoOutOfStock}>
        <Typography variant="overline" style={{ paddingLeft: 5 }}>
          no hay más unidades
        </Typography>
      </Collapse>
    </Grid>
  );
};

export default MultipleOptionButton;
